import React from 'react'
import { Join } from '../../lib/utils'

/**
 * `<Card>` — base surface primitive used by every other card in the system.
 *
 * Pure CSS-token output: `bg-surface text-text border-border`. No Tailwind
 * `dark:` prefixes. Override the look via `tone` (a state colour swap) or by
 * passing `className`.
 *
 * Server-component safe.
 */

export type CardTone =
  'default' | 'accent' | 'info' | 'success' | 'warning' | 'danger' | 'muted'

/** `glass` — frosted backdrop surface for marketing sections over canvases/gradients. */
export type CardSurface = 'solid' | 'glass'

/** Border tint when `surface="glass"`. `accent` matches speclogician blue glass cards. */
export type CardGlassVariant = 'neutral' | 'accent'

const toneSurface: Record<CardTone, string> = {
  default: 'bg-surface border-border',
  accent: 'bg-accent-muted border-accent/30',
  info: 'bg-info/10 border-info/30 dark:bg-info/15',
  success: 'bg-success/10 border-success/30 dark:bg-success/15',
  warning: 'bg-warning/10 border-warning/30 dark:bg-warning/15',
  danger: 'bg-danger/10 border-danger/30 dark:bg-danger/15',
  muted: 'bg-surface-sunken border-border',
}

const toneText: Record<CardTone, string> = {
  default: 'text-text',
  accent: 'text-accent',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  muted: 'text-text-muted',
}

const glassSurface: Record<CardGlassVariant, string> = {
  neutral:
    'border-slate-200/25 bg-white/40 backdrop-blur-md dark:border-white/5 dark:bg-slate-900/20',
  accent:
    'border-accent/30 bg-white/40 backdrop-blur-md dark:border-accent/25 dark:bg-slate-900/20',
}

export interface CardProps {
  children: React.ReactNode
  tone?: CardTone
  surface?: CardSurface
  /** When `surface="glass"`. Default `neutral`. */
  glassVariant?: CardGlassVariant
  /** Apply hover lift / border highlight. Default true. */
  interactive?: boolean
  /** Padding preset. Default `md`. */
  padding?: 'none' | 'sm' | 'md' | 'lg'
  /** Border radius preset. Default `lg`. */
  radius?: 'none' | 'md' | 'lg' | 'xl' | '2xl'
  className?: string
  id?: string
  as?: 'div' | 'article' | 'section'
}

const padClass = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
} as const

const radiusClass = {
  none: 'rounded-none',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
} as const

export function Card({
  children,
  tone = 'default',
  surface = 'solid',
  glassVariant = 'neutral',
  interactive = false,
  padding = 'md',
  radius = 'lg',
  className,
  id,
  as = 'div',
}: CardProps) {
  const isGlass = surface === 'glass'
  return React.createElement(
    as,
    {
      id,
      className: Join(
        'font-display',
        'border',
        isGlass ? glassSurface[glassVariant] : toneSurface[tone],
        toneText[tone],
        padClass[padding],
        radiusClass[radius],
        interactive
          ? 'transition-all duration-200 hover:border-accent/60 hover:shadow-md'
          : '',
        className,
      ),
    },
    children,
  )
}

// ── Card parts ─────────────────────────────────────────────────────────────
//
// Lightweight wrappers used by composed cards (FeatureCard, InfoCard, …).
// They're intentionally thin so consumers can compose their own card layouts.

export function CardIcon({
  children,
  className,
  tone = 'accent',
}: {
  children: React.ReactNode
  className?: string
  tone?: 'accent' | 'muted'
}) {
  const toneClasses =
    tone === 'accent'
      ? 'bg-accent-muted text-accent'
      : 'bg-surface-sunken text-text-muted'
  return (
    <div
      className={Join(
        'mb-4 inline-flex items-center justify-center rounded-lg p-3',
        toneClasses,
        className,
      )}
    >
      {children}
    </div>
  )
}

export function CardTitle({
  children,
  className,
  as = 'h3',
}: {
  children: React.ReactNode
  className?: string
  as?: 'h2' | 'h3' | 'h4' | 'p'
}) {
  return React.createElement(
    as,
    {
      // Colour inherits from `<Card tone>` (`toneText` on the root).
      className: Join('mb-2 text-lg font-semibold tracking-tight', className),
    },
    children,
  )
}

export function CardBody({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={Join(
        // Inherit Card tone colour; soften so body sits under the title.
        'text-[0.95em] leading-relaxed opacity-80',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function CardFooter({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={Join(
        'border-border text-text-subtle mt-4 flex items-center gap-2 border-t pt-3 text-xs',
        className,
      )}
    >
      {children}
    </div>
  )
}
